// Cover crossfade, and the single instant every other layer hangs off.
//
// WHAT IT REPLACED, and why the hole was unavoidable before: the cover was ONE
// div whose background-image was reassigned on every track change. That is not
// quite the "img whose src changes" the brief guessed at - it is worse. An img
// keeps painting its previous frame until the new one decodes; a
// background-image does not. The moment the property was set, the element
// painted its bare rgba(255,255,255,0.08) placeholder and kept painting it
// until the bitmap came back off the network. The gap WAS the download.
//
// So the fix is not a nicer animation over the same swap - it is to stop
// swapping anything until the new bitmap is decoded and ready to paint, and to
// have somewhere for it to land that is not the element still showing the old
// one. Hence two layers, and decode() before anything moves.
//
// This module also owns the ONE timestamp the rest of the app aligns to. The
// scene's palette and the light rig's dip used to fire off the raw track
// change, which happens BEFORE the download - so the colours swung while the
// old cover was still on screen. Everything now subscribes to
// onArtworkTransitionStart() instead, which fires at the instant the crossfade
// actually begins.

const FADE_MS = 700;
const FADE_EASING = 'cubic-bezier(0.4, 0.0, 0.2, 1)';
// The incoming layer starts this long AFTER the outgoing one, so the two
// overlap rather than hand over at an instant.
const ENTER_DELAY_MS = 120;

// The outgoing layer HOLDS full opacity for this fraction of its fade before
// it starts dropping, and this is a deliberate departure from the brief's
// "sortante: opacite 1 -> 0" read literally.
//
// Measured: with both layers fading linearly from the start, the composite
// coverage - 1-(1-a)(1-b) for two stacked opaque images - bottomed out at
// 0.502 at +263ms. For about a fifth of a second you saw the visualizer
// THROUGH a half-transparent cover. That is never a coverless frame, so the
// letter of the criterion held, but a 50% see-through flash is exactly the
// hole the transition exists to remove. The 120ms stagger narrows that dip; it
// cannot remove it, because two opaque layers crossing linearly always dip.
//
// Holding the outgoing opaque until the incoming has covered it costs nothing
// visually: the incoming starts at scale 1.06, LARGER than the frame, so it
// covers the outgoing's own shrink to 0.94 at every instant. The outgoing
// still travels its full 1 -> 0.94 / 0 -> -8px path, so the motion the brief
// described is unchanged - only the moment its opacity starts falling moves.
const EXIT_HOLD_FRACTION = 0.45;
const TEXT_DELAY_MS = 80;
const TEXT_SHIFT_PX = 6;
// Past this, the network has lost and we keep what is on screen. A stale cover
// under a correct title reads as a lag; a hole reads as a fault.
const DECODE_TIMEOUT_MS = 4000;
// Slower than the cover on purpose: the halo is the only thing continuous
// across the swap, so it carries the eye while the two images cross.
const HALO_MS = 1100;

const listeners = [];
let layers = null;      // [A, B]
let activeLayer = 0;
let haloRaf = null;
let currentHalo = null; // {r, g, b} actually on screen right now
let pendingToken = 0;

// Fires when the crossfade STARTS - not when the track changed, not when the
// image arrived. Subscribers: the artwork palette handed to the visualizers,
// and the light rig's own dip. One instant, one event, no second guess.
export function onArtworkTransitionStart(fn) {
  listeners.push(fn);
}

function emitTransitionStart(detail) {
  for (let i = 0; i < listeners.length; i++) {
    try {
      listeners[i](detail);
    } catch (err) {
      // A subscriber must never be able to strand the crossfade half-done.
      console.error('[artwork-transition] listener failed', err);
    }
  }
}

export function initArtworkTransition(layerA, layerB) {
  layers = [layerA, layerB];
  activeLayer = 0;
  layerA.style.opacity = '1';
  layerB.style.opacity = '0';
}

// Decode, not merely load. onload fires once the bytes are in; decode() waits
// until the bitmap is ready to paint, which is the difference between the
// first crossfade frame being free and it costing a decode mid-animation.
// Resolves false on failure OR timeout - the caller keeps the current cover
// either way.
function decodeImage(url) {
  return new Promise(function (resolve) {
    let settled = false;
    const done = function (ok) {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    const timer = setTimeout(function () { done(false); }, DECODE_TIMEOUT_MS);
    const img = new Image();
    // NO crossOrigin. This image is only ever painted, as a CSS
    // background-image - nothing reads its pixels back, so it needs no CORS
    // grant, and asking for one is not free: crossOrigin='anonymous' makes the
    // load FAIL outright when the server does not answer with
    // Access-Control-Allow-Origin. The real covers come from Spotify's CDN
    // (mosaic.scdn.co), which does not, so every decode failed, every
    // transition fell into its keep-what-is-on-screen path, and at startup
    // that meant no cover at all - ever. The palette extractor is the only
    // thing here that reads pixels, and it has its own CORS-safe route through
    // /api/art-proxy.
    img.onerror = function () { clearTimeout(timer); done(false); };
    img.src = url;
    const finish = function (ok) { clearTimeout(timer); done(ok); };
    if (typeof img.decode === 'function') {
      img.decode().then(function () { finish(true); }, function () {
        // decode() rejects on some cross-origin/animated cases where the image
        // is nonetheless perfectly paintable - fall back rather than dropping
        // a cover that would have worked.
        finish(img.complete && img.naturalWidth > 0);
      });
    } else {
      img.onload = function () { finish(true); };
    }
  });
}

// Preloads without touching the screen, so the bitmap is already in the cache
// by the time the swap is wanted. Safe to call repeatedly with the same URL.
const warmed = new Set();
export function warmArtwork(url) {
  if (!url || warmed.has(url)) return;
  warmed.add(url);
  if (warmed.size > 8) warmed.delete(warmed.values().next().value);
  const img = new Image();
  img.src = url;
}

function clearAnimations(el) {
  el.getAnimations().forEach(function (a) { a.cancel(); });
}

// Reads what is ACTUALLY on screen right now rather than what the last
// transition intended. Two track changes a second apart land here mid-flight,
// and restarting from the committed style is what stops a layer being stranded
// at half opacity.
function currentOpacity(el) {
  const computed = Number(getComputedStyle(el).opacity);
  return Number.isFinite(computed) ? computed : 1;
}

function runFade(incoming, outgoing) {
  clearAnimations(incoming);
  clearAnimations(outgoing);

  const outFrom = currentOpacity(outgoing);
  const inFrom = currentOpacity(incoming);

  incoming.style.willChange = 'opacity, transform';
  outgoing.style.willChange = 'opacity, transform';

  // The incoming layer must paint ON TOP, and which element that is alternates
  // every track. Without this the layer that happens to come first in the DOM
  // would be covered by the one fading out above it, and holding the outgoing
  // opaque (see EXIT_HOLD_FRACTION) would hide the new cover completely.
  incoming.style.zIndex = '2';
  outgoing.style.zIndex = '1';

  // TWO animations on the outgoing layer, not one, and the split is the
  // difference between the hold working and not working. With a single
  // animation the effect easing remaps overall progress BEFORE the keyframe
  // offsets are read, so a hold written as offset 0.45 is reached at 0.25 of
  // wall time under cubic-bezier(0.4,0,0.2,1) - measured composite still dipped
  // to 0.671. Opacity therefore runs on its own linear timeline, where the hold
  // means what it says in milliseconds; the transform keeps the shared curve,
  // which is the part anyone can actually see.
  const outTransform = outgoing.animate([
    { transform: 'scale(1) translateY(0px)' },
    { transform: 'scale(0.94) translateY(-8px)' }
  ], { duration: FADE_MS, easing: FADE_EASING, fill: 'forwards' });

  const outAnim = outgoing.animate([
    { opacity: outFrom, offset: 0 },
    { opacity: outFrom, offset: EXIT_HOLD_FRACTION },
    { opacity: 0, offset: 1 }
  ], { duration: FADE_MS, easing: 'linear', fill: 'forwards' });

  const inAnim = incoming.animate([
    { opacity: inFrom, transform: 'scale(1.06) translateY(8px)' },
    { opacity: 1, transform: 'scale(1) translateY(0px)' }
  ], { duration: FADE_MS, easing: FADE_EASING, delay: ENTER_DELAY_MS, fill: 'forwards' });

  // Commit the end state to inline style and drop will-change: a compositor
  // layer held for the whole session costs memory on a TV for no benefit once
  // nothing is moving.
  const settle = function () {
    if (incoming.style.opacity === '1' && outgoing.style.opacity === '0') return;
  };
  inAnim.finished.then(function () {
    incoming.style.opacity = '1';
    incoming.style.transform = 'none';
    incoming.style.willChange = 'auto';
    settle();
  }, function () { /* cancelled by a newer transition - it owns the cleanup */ });
  outAnim.finished.then(function () {
    outgoing.style.opacity = '0';
    outgoing.style.willChange = 'auto';
  }, function () { /* cancelled - see above */ });
  outTransform.finished.then(function () {}, function () {});
}

export function animateText(elements) {
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    if (!el) continue;
    clearAnimations(el);
    el.style.willChange = 'opacity, transform';
    const anim = el.animate([
      { opacity: 0, transform: 'translateY(' + TEXT_SHIFT_PX + 'px)' },
      { opacity: 1, transform: 'translateY(0px)' }
    ], { duration: FADE_MS, easing: FADE_EASING, delay: TEXT_DELAY_MS, fill: 'both' });
    anim.finished.then(function () { el.style.willChange = 'auto'; }, function () {});
  }
}

// The halo is three CSS custom properties read by #overlay-artwork's gradient
// pseudo-elements. Interpolated in JS rather than by a CSS transition because
// plain custom properties are not interpolatable without @property, and this
// page has to run on a TV whose engine version is not ours to assume. Three
// number writes per frame for just over a second is nothing.
export function transitionHalo(target, apply) {
  if (haloRaf !== null) cancelAnimationFrame(haloRaf);
  const from = currentHalo || target;
  const start = performance.now();
  const step = function (now) {
    const t = Math.min(1, (now - start) / HALO_MS);
    // Same shape as the cover's easing, close enough that the two read as one
    // gesture rather than two timings.
    const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    const mix = {
      r: Math.round(from.r + (target.r - from.r) * e),
      g: Math.round(from.g + (target.g - from.g) * e),
      b: Math.round(from.b + (target.b - from.b) * e)
    };
    currentHalo = mix;
    apply(mix);
    if (t < 1) haloRaf = requestAnimationFrame(step);
    else haloRaf = null;
  };
  haloRaf = requestAnimationFrame(step);
}

export function setHaloImmediate(color, apply) {
  if (haloRaf !== null) { cancelAnimationFrame(haloRaf); haloRaf = null; }
  currentHalo = color;
  apply(color);
}

// The entry point. Decodes first, and only then touches the screen - so the
// cover currently displayed stays displayed for as long as the network takes,
// and there is no instant with nothing on it.
//
// url === null is a real state (a track with no cover art), not an error: it
// crossfades to the empty placeholder layer with the same animation rather
// than blanking.
export function requestArtwork(url, detail) {
  if (!layers) return Promise.resolve(false);
  const token = ++pendingToken;

  const swap = function () {
    // A newer request overtook this one while it was decoding. Drop this one
    // entirely: the newer one is already the truth.
    if (token !== pendingToken) return false;
    const outgoing = layers[activeLayer];
    const incoming = layers[1 - activeLayer];
    incoming.style.backgroundImage = url ? 'url("' + url + '")' : '';
    activeLayer = 1 - activeLayer;
    emitTransitionStart(detail || {});
    runFade(incoming, outgoing);
    return true;
  };

  if (!url) return Promise.resolve(swap());
  return decodeImage(url).then(function (ok) {
    // Decode failed or timed out: keep the cover that is already up. The title
    // has already changed on its own, which reads as a lag rather than a fault
    // - and a hole is worse than a lag.
    if (!ok) {
      if (token === pendingToken) emitTransitionStart(detail || {});
      return false;
    }
    return swap();
  });
}

export const ARTWORK_FADE_MS = FADE_MS;
